import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { SAAuthProvider } from "./contexts/SAAuthContext";
import { Toaster } from "sonner";
import { ProtectedRoute } from "./components/tabon/ProtectedRoute";
import { SAProtectedRoute } from "./components/superadmin/SAProtectedRoute";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import StaffLogin from "./pages/StaffLogin";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Billing from "./pages/Billing";
import Customers from "./pages/Customers";
import Promotions from "./pages/Promotions";
import Rewards from "./pages/Rewards";
import WhatsAppLog from "./pages/WhatsAppLog";
import Settings from "./pages/Settings";
import MenuPage from "./pages/Menu";
import ItemCodes from "./pages/ItemCodes";
import ItemTaxGroups from "./pages/ItemTaxGroups";
import ItemAvailability from "./pages/ItemAvailability";
import Reports from "./pages/Reports";
import PublicMenu from "./pages/PublicMenu";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import OrderStatus from "./pages/OrderStatus";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ActivateAccount from "./pages/ActivateAccount";
import MenuAvailability from "./pages/MenuAvailability";
import JoinVenue from "./pages/JoinVenue";
import Split from "./pages/Split";
import { Orders } from "./components/tabon/Orders";
import Tables from "./pages/Tables";
import Kitchen from "./pages/Kitchen";
import Invoice from "./pages/Invoice";

import SALogin from "./pages/admin/SALogin";
import SASetPassword from "./pages/admin/SASetPassword";
import SAResetPassword from "./pages/admin/SAResetPassword";
import SADashboard from "./pages/admin/SADashboard";
import SAVenueDirectory from "./pages/admin/SAVenueDirectory";
import SAVenueDetail from "./pages/admin/SAVenueDetail";
import SAPipeline from "./pages/admin/SAPipeline";
import SABilling from "./pages/admin/SABilling";
import SAAnalytics from "./pages/admin/SAAnalytics";
import SATeamManagement from "./pages/admin/SATeamManagement";
import SAConfig from "./pages/admin/SAConfig";
import SATemplateHealth from "./pages/admin/SATemplateHealth";

import "@/App.css";

function AppRouter() {
  const location = useLocation();
  // OAuth callback with session_id in URL fragment — handle synchronously
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Superadmin portal — isolated auth context, /admin/* routes only */}
      <Route path="/admin/login" element={<SALogin />} />
      <Route path="/admin/invite/:token" element={<SASetPassword />} />
      <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
      <Route path="/admin/dashboard" element={<SAProtectedRoute><SADashboard /></SAProtectedRoute>} />
      <Route path="/admin/venues" element={<SAProtectedRoute><SAVenueDirectory /></SAProtectedRoute>} />
      <Route path="/admin/venues/:vid" element={<SAProtectedRoute><SAVenueDetail /></SAProtectedRoute>} />
      <Route path="/admin/pipeline" element={<SAProtectedRoute><SAPipeline /></SAProtectedRoute>} />
      <Route path="/admin/billing" element={<SAProtectedRoute><SABilling /></SAProtectedRoute>} />
      <Route path="/admin/analytics" element={<SAProtectedRoute><SAAnalytics /></SAProtectedRoute>} />
      <Route path="/admin/team" element={<SAProtectedRoute><SATeamManagement /></SAProtectedRoute>} />
      <Route path="/admin/config" element={<SAProtectedRoute adminOnly><SAConfig /></SAProtectedRoute>} />
      <Route path="/admin/template-health" element={<SAProtectedRoute adminOnly><SATemplateHealth /></SAProtectedRoute>} />
      <Route path="/admin/reset-password" element={<SAResetPassword />} />

      {/* Venue/Staff app */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/staff-login" element={<StaffLogin />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/activate" element={<ActivateAccount />} />

      <Route path="/dashboard" element={<ProtectedRoute adminOnly><Dashboard /></ProtectedRoute>} />
      <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
      <Route path="/tables" element={<ProtectedRoute><Tables /></ProtectedRoute>} />
      <Route path="/kitchen" element={<ProtectedRoute><Kitchen /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute adminOnly><Reports /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute adminOnly><Customers /></ProtectedRoute>} />
      <Route path="/promotions" element={<ProtectedRoute adminOnly><Promotions /></ProtectedRoute>} />
      <Route path="/rewards" element={<ProtectedRoute adminOnly><Rewards /></ProtectedRoute>} />
      <Route path="/whatsapp-log" element={<ProtectedRoute adminOnly><WhatsAppLog /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute adminOnly><Settings /></ProtectedRoute>} />
      <Route path="/menu" element={<ProtectedRoute adminOnly><MenuPage /></ProtectedRoute>} />
      <Route path="/menu/item-codes" element={<ProtectedRoute adminOnly><ItemCodes /></ProtectedRoute>} />
      <Route path="/menu/tax-groups" element={<ProtectedRoute adminOnly><ItemTaxGroups /></ProtectedRoute>} />
      <Route path="/menu/availability" element={<ProtectedRoute adminOnly><ItemAvailability /></ProtectedRoute>} />
      <Route path="/menu-availability" element={<ProtectedRoute><MenuAvailability /></ProtectedRoute>} />

      {/* Public menu (no auth) */}
      <Route path="/menu/:slug" element={<PublicMenu />} />
      {/* Delivery / loyalty registration (no auth) */}
      <Route path="/join/:slug" element={<JoinVenue />} />
      {/* Points split (no auth) */}
      <Route path="/split/:token" element={<Split />} />
      {/* Legal pages (no auth) */}
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms/:slug" element={<Terms />} />

      {/* Public invoice page (no auth) */}
      <Route path="/invoice/:orderId" element={<Invoice />} />

      {/* Online-payment return / status page (no auth) */}
      <Route path="/order/:orderId" element={<OrderStatus />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <SAAuthProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster
              theme="dark"
              position="top-right"
              toastOptions={{
                style: {
                  background: "#1A1A1A",
                  border: "1px solid rgba(157, 0, 255, 0.3)",
                  color: "#fff",
                },
              }}
            />
          </BrowserRouter>
        </SAAuthProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
